/**
 * Plain helpers for User Administration: dates, names, page access, filters,
 * activity wording and the Excel sheets. No React in here.
 */

export const MIN = 60000;
export const HOUR = 3600000;
export const DAY = 86400000;

// A page is on (they can open it and work in it) or off (restricted).
export const LV = ['Off', 'On'];
export const ST_LABEL = { active: 'Active', leaving: 'Leaving', invited: 'Invited', deactivated: 'Deactivated', unmanaged: 'Not set up' };
const STATUS_ORDER = ['active', 'leaving', 'invited', 'deactivated'];

/* ---------------- dates ---------------- */

export const ms = (iso) => (iso ? Date.parse(iso) || 0 : 0);
const pad = (n) => String(n).padStart(2, '0');
export const isoDay = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
export const isoIn = (days) => { const d = new Date(); d.setDate(d.getDate() + days); return isoDay(d); };
export const dayMs = (iso) => { const [y, m, d] = String(iso).split('-').map(Number); return new Date(y, m - 1, d).getTime(); };
const dayStart = (t) => { const d = new Date(t); d.setHours(0, 0, 0, 0); return d.getTime(); };
export const dayDiff = (t) => Math.round((dayStart(Date.now()) - dayStart(t)) / DAY);
export const fmtTime = (t) => new Date(t).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
export const fmtDate = (t) => new Date(t).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
export const fmtShort = (t) => new Date(t).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

/** "just now", "5 min ago", "2 h ago", "yesterday", "3 days ago", "2 weeks ago", then the date. */
export function rel(t) {
    const diff = Date.now() - t;
    const dd = dayDiff(t);
    if (diff < MIN) return 'just now';
    if (dd <= 0) return diff < HOUR ? `${Math.round(diff / MIN)} min ago` : `${Math.round(diff / HOUR)} h ago`;
    if (dd === 1) return 'yesterday';
    if (dd < 7) return `${dd} days ago`;
    if (dd < 28) return `${Math.floor(dd / 7)} ${dd < 14 ? 'week' : 'weeks'} ago`;
    return fmtShort(t);
}

export const plural = (n, word, words) => `${n} ${n === 1 ? word : (words || `${word}s`)}`;

/* ---------------- people ---------------- */

export const fullName = (p) => (p ? `${p.first || ''} ${p.last || ''}`.trim() || p.email : 'Someone');
export const stKey = (p) => (p.status === 'active' && p.scheduledOff ? 'leaving' : p.status);
export const roleById = (data, id) => data.roles.find((r) => r.id === id);
export const roleName = (data, id) => (roleById(data, id) || {}).name || 'No role';
export const personById = (data, id) => data.people.find((p) => p.id === id);
export const roleMembers = (data, id) => data.people.filter((p) => p.role === id && p.status !== 'deactivated');
export const nOverrides = (p) => Object.keys(p.overrides || {}).length;

/** A person's departments as a list. Stored like the user table: "Warehouse, Inspection". */
export const deptList = (value) => (Array.isArray(value) ? value : String(value || '').split(','))
    .map((d) => String(d).trim()).filter(Boolean);
export const joinDepts = (list) => deptList(list).join(', ');

/** The role suggested for these departments: the first one that has a suggestion. */
export const suggestedRole = (catalog, depts) => {
    const d = deptList(depts).find((x) => catalog.deptRole[x]);
    return d ? { role: catalog.deptRole[d], dept: d } : null;
};

/** Splits "first.last@company.com" into a name, for people the users backend knows only by email. */
export function nameFromEmail(email, name) {
    const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
    if (parts.length > 1) return { first: parts[0], last: parts.slice(1).join(' ') };
    const local = String(email || '').split('@')[0].split(/[._-]+/).filter(Boolean)
        .map((s) => s.charAt(0).toUpperCase() + s.slice(1));
    return { first: parts[0] || local[0] || '', last: local.slice(1).join(' ') };
}

/** The small line under a status pill. */
export function statusSub(p) {
    const k = stKey(p);
    if (k === 'invited') return 'Hasn’t signed in yet';
    if (k === 'leaving') return `Last day ${fmtShort(dayMs(p.scheduledOff))}`;
    if (k === 'deactivated') return `Since ${fmtShort(ms(p.deactivatedAt))}`;
    return p.lastActive ? `Active ${rel(ms(p.lastActive))}` : 'No sign-in since set up';
}

/* ---------------- access ---------------- */

export const allPages = (catalog) => catalog.sections.reduce((all, s) => all.concat(s.pages), []);

export function lvlOf(data, roleId, overrides, pageId) {
    const role = roleById(data, roleId);
    if (role && role.admin) return 1;
    if (overrides && Object.prototype.hasOwnProperty.call(overrides, pageId)) return overrides[pageId];
    return role ? role.levels[pageId] || 0 : 0;
}

export const openCount = (data, catalog, roleId, overrides) =>
    allPages(catalog).filter((p) => lvlOf(data, roleId, overrides, p.id) > 0).length;

export function sameOverrides(a, b) {
    const ka = Object.keys(a || {}).sort();
    const kb = Object.keys(b || {}).sort();
    return ka.length === kb.length && ka.every((k, i) => k === kb[i] && a[k] === b[k]);
}

/* ---------------- users filters ---------------- */

export const EMPTY_FILTERS = { q: '', dept: 'all', role: 'all', status: 'all', sort: 'name', dir: 1 };
export const anyFilter = (f) => !!(f.q.trim() || f.dept !== 'all' || f.role !== 'all' || f.status !== 'all');

function matchStatus(p, status) {
    if (status === 'all') return true;
    const k = stKey(p);
    if (status === 'active') return k === 'active' || k === 'leaving';
    return k === status;
}

const SORT_KEYS = {
    name: (p) => fullName(p).toLowerCase(),
    dept: (p) => `${p.dept} ${fullName(p)}`.toLowerCase(),
    role: (p, data) => `${roleName(data, p.role)} ${fullName(p)}`.toLowerCase(),
    // Status order first, then the most recently active.
    status: (p) => STATUS_ORDER.indexOf(stKey(p)) * 1e13 - ms(p.lastActive),
};

export function visibleUsers(data, f) {
    const q = f.q.trim().toLowerCase();
    const key = SORT_KEYS[f.sort] || SORT_KEYS.name;
    return data.people
        .filter((p) => (f.dept === 'all' || deptList(p.dept).includes(f.dept)) && (f.role === 'all' || p.role === f.role) && matchStatus(p, f.status)
            && (!q || `${fullName(p)} ${p.email} ${p.title || ''}`.toLowerCase().indexOf(q) !== -1))
        .sort((a, b) => {
            const x = key(a, data);
            const y = key(b, data);
            return (x < y ? -1 : x > y ? 1 : 0) * f.dir;
        });
}

export function statusCounts(data) {
    const c = { active: 0, invited: 0, leaving: 0, deactivated: 0 };
    data.people.forEach((p) => {
        const k = stKey(p);
        if (k === 'leaving') { c.active += 1; c.leaving += 1; } else if (c[k] != null) c[k] += 1;
    });
    return c;
}

export const unmanagedMatches = (list, q) => {
    const s = q.trim().toLowerCase();
    return list.filter((u) => !s || `${u.first} ${u.last} ${u.email}`.toLowerCase().indexOf(s) !== -1);
};

/* ---------------- activity ---------------- */

export const LABEL = {
    add: 'Added', update: 'Profile updated', role: 'Role changed', access: 'Page access changed',
    rolePerm: 'Role pages changed', roleNew: 'Role created', roleRename: 'Role renamed', deact: 'Deactivated',
    schedule: 'Last day set', unschedule: 'Last day cancelled', react: 'Reactivated', invite: 'Invite copied',
    signin: 'First sign-in', undo: 'Change undone',
};
export const labelOf = (type) => LABEL[type] || 'Change';

export const TYPE_FILTERS = [
    ['all', 'All changes', null],
    ['add', 'Added', ['add', 'signin']],
    ['role', 'Role changes', ['role']],
    ['access', 'Page access', ['access', 'rolePerm', 'roleNew', 'roleRename']],
    ['update', 'Profile updates', ['update']],
    ['status', 'Deactivated & reactivated', ['deact', 'react', 'schedule', 'unschedule']],
];
export const RANGES = [['7', 'Last 7 days'], ['30', 'Last 30 days'], ['all', 'All time']];
export const EMPTY_ACT = { q: '', type: 'all', range: '30' };

const isRoleEntry = (e) => e.type === 'rolePerm' || e.type === 'roleNew' || e.type === 'roleRename';

export function actorLabel(e, data) {
    if (e.actor === 'system') return 'Bridge';
    const p = data.people.find((x) => x.email === e.actor);
    return p ? fullName(p) : (e.actorName || e.actor);
}

export function targetLabel(e, data) {
    if (isRoleEntry(e)) return `${(roleById(data, e.target) || {}).name || e.targetName} (role)`;
    const p = personById(data, e.target);
    return p ? fullName(p) : (e.targetName || '—');
}

export function activityFiltered(data, af) {
    const types = (TYPE_FILTERS.find((t) => t[0] === af.type) || TYPE_FILTERS[0])[2];
    const q = af.q.trim().toLowerCase();
    const since = af.range === 'all' ? 0 : Date.now() - Number(af.range) * DAY;
    return data.log.filter((e) => (!types || types.indexOf(e.type) !== -1) && ms(e.at) >= since
        && (!q || `${targetLabel(e, data)} ${actorLabel(e, data)}`.toLowerCase().indexOf(q) !== -1));
}

export const whenDay = (t) => (dayDiff(t) === 0 ? 'Today' : dayDiff(t) === 1 ? 'Yesterday' : fmtDate(t));

/* ---------------- Excel ---------------- */

export function usersSheet(people, data) {
    return {
        name: 'Users',
        head: ['Name', 'Email', 'Job title', 'Department', 'Role', 'Status', 'Last active', 'Pages changed for them', 'Added on'],
        rows: people.map((p) => [
            fullName(p), p.email, p.title || '', p.dept, roleName(data, p.role), ST_LABEL[stKey(p)],
            p.lastActive ? fmtDate(ms(p.lastActive)) : 'Never', nOverrides(p) || '', fmtDate(ms(p.createdAt)),
        ]),
    };
}

export const unmanagedSheet = (list) => ({
    name: 'Not set up yet',
    head: ['Name', 'Email'],
    rows: list.map((u) => [`${u.first} ${u.last}`.trim(), u.email]),
});

export function activitySheet(entries, data) {
    return {
        name: 'Activity log',
        head: ['When', 'Changed by', 'User or role', 'Change', 'Details'],
        rows: entries.map((e) => [
            `${fmtDate(ms(e.at))}, ${fmtTime(ms(e.at))}`, actorLabel(e, data), targetLabel(e, data), labelOf(e.type),
            e.changes.map((c) => `${c[0]}: ${c[1] ? `${c[1]} → ` : ''}${c[2]}`).concat(e.note ? [e.note] : []).join('; '),
        ]),
    };
}

/* ---------------- invite ---------------- */

/**
 * The message an IT Manager pastes into an email or Teams chat for someone new. `password` is the
 * temporary password typed when they were added; it is only known right after adding, never stored.
 */
export function inviteMessage(p, password) {
    return [
        `Hi ${p.first},`,
        '',
        `You now have access to Bridge. Sign in at ${window.location.origin} with your work email, ${p.email}.`,
        ...(password ? ['', `Your temporary password is: ${password}`] : []),
        '',
        'If you can’t sign in, reply and IT will help.',
    ].join('\n');
}

export async function copyText(value) {
    try {
        await navigator.clipboard.writeText(value);
        return true;
    } catch (err) {
        const area = document.createElement('textarea');
        area.value = value;
        area.setAttribute('readonly', '');
        area.style.position = 'fixed';
        area.style.opacity = '0';
        document.body.appendChild(area);
        area.select();
        let copied = false;
        try { copied = document.execCommand('copy'); } catch (e) { copied = false; }
        document.body.removeChild(area);
        return copied;
    }
}
